"use client";

import React, { memo, DragEvent } from "react";
import { Item, ItemType, ElementPositioningProps } from "@thenounproject/lingo-core";
import { useIsItemSelected } from "@contexts/SelectedItemsProvider";
import { getVersionedItemId } from "@helpers/items";

import CanvasNote from "./CanvasNote";
import CanvasGuide from "./CanvasGuide";
import CanvasSupportContent from "./CanvasSupportContent";
import CanvasCodeSnippet from "./CanvasCodeSnippet";
import CanvasHeading from "./CanvasHeading";
import CanvasGallery from "./CanvasGallery";
import CanvasAsset from "./CanvasAsset";

type GalleryOutlinePosition = "full" | "left" | "right";

export type SelectableCanvasItemProps = {
  item: Item;
  globalIndex: number;
  items: Item[];
  galleryOutline: Record<string, { position: GalleryOutlinePosition }>;
  editingItem: string | null;
  canEditContent: boolean;
  canGenerateText: boolean;

  // Callbacks - these should be stable (wrapped in useCallback in parent)
  onOverflowSelected: (item: Item) => void;
  onItemDoubleClicked: (item: Item, e: React.MouseEvent) => void;
  onItemClicked: (item: Item, e: React.MouseEvent) => void;
  onUseButtonSelected: (item: Item, e: React.MouseEvent) => void;
  handleReorderDragEnd: (e: DragEvent<HTMLElement>) => void;
  handleReorderDragStart: (e: DragEvent<HTMLElement>) => void;
  onStartEditing: (itemId: string) => void;
  onFinishEditing: (item: Item, data: unknown) => void;

  // Spacing props computed by parent
  spacingProps: ElementPositioningProps;
  guidePosition?: GalleryOutlinePosition;
};

/**
 * Wrapper component that handles selection subscription for individual canvas items.
 * This component subscribes only to its own item's selection state, preventing
 * re-renders when other items are selected/deselected.
 */
const SelectableCanvasItem: React.FC<SelectableCanvasItemProps & { containerWidth: number }> = ({
  item,
  globalIndex,
  editingItem,
  canEditContent,
  canGenerateText,
  onOverflowSelected,
  onItemDoubleClicked,
  onItemClicked,
  onUseButtonSelected,
  handleReorderDragEnd,
  handleReorderDragStart,
  onStartEditing,
  onFinishEditing,
  spacingProps,
  guidePosition = "full",
  containerWidth,
}) => {
  const versionedId = getVersionedItemId(item);
  // Subscribe to just this item's selection state
  const isSelected = useIsItemSelected("kitGallery", versionedId);
  const isEditing = editingItem === item.id;

  switch (item.type) {
    case ItemType.asset: {
      return (
        <CanvasAsset
          containerWidth={containerWidth}
          item={item}
          index={globalIndex}
          onOverflowSelected={onOverflowSelected}
          onDoubleClick={onItemDoubleClicked}
          isSelected={isSelected}
          canEdit={canEditContent}
          canGenerateText={canGenerateText}
          onClick={onItemClicked}
          onUseButtonSelected={onUseButtonSelected}
          draggable={canEditContent && !isEditing}
          onDragEnd={handleReorderDragEnd}
          onDragStart={handleReorderDragStart}
          isEditing={isEditing}
          onBeginEditing={onStartEditing}
          onFinishEditing={onFinishEditing}
          {...spacingProps}
        />
      );
    }

    case ItemType.supportingImage: {
      return (
        <CanvasSupportContent
          containerWidth={containerWidth}
          item={item}
          index={globalIndex}
          onOverflowSelected={onOverflowSelected}
          onClick={onItemClicked}
          draggable={canEditContent && !isEditing}
          onDragEnd={handleReorderDragEnd}
          onDragStart={handleReorderDragStart}
          isSelected={isSelected}
          canEdit={canEditContent}
          {...spacingProps}
        />
      );
    }

    case ItemType.heading: {
      return (
        <CanvasHeading
          item={item}
          index={globalIndex}
          {...spacingProps}
          draggable={canEditContent && !isEditing}
          onDragStart={handleReorderDragStart}
          onDragEnd={handleReorderDragEnd}
          isSelected={isSelected}
          onClick={onItemClicked}
          onDoubleClick={onItemDoubleClicked}
          onBeginEditing={onStartEditing}
          onFinishEditing={onFinishEditing}
          canEdit={canEditContent}
          isEditing={isEditing}
          onOverflowSelected={onOverflowSelected}
        />
      );
    }

    case ItemType.guide: {
      return (
        <CanvasGuide
          containerWidth={containerWidth}
          {...spacingProps}
          item={item}
          index={globalIndex}
          guidePosition={guidePosition}
          onOverflowSelected={onOverflowSelected}
          onClick={onItemClicked}
          draggable={canEditContent && !isEditing}
          onDragEnd={handleReorderDragEnd}
          onDragStart={handleReorderDragStart}
          isSelected={isSelected}
          canEdit={canEditContent}
          canGenerateText={canGenerateText}
          isEditing={isEditing}
          onBeginEditing={onStartEditing}
          onFinishEditing={onFinishEditing}
        />
      );
    }

    case ItemType.codeSnippet: {
      return (
        <CanvasCodeSnippet
          containerWidth={containerWidth}
          item={item}
          index={globalIndex}
          {...spacingProps}
          draggable={canEditContent && !isEditing}
          onDragStart={handleReorderDragStart}
          onDragEnd={handleReorderDragEnd}
          isSelected={isSelected}
          onClick={onItemClicked}
          onDoubleClick={onItemDoubleClicked}
          onBeginEditing={onStartEditing}
          onFinishEditing={onFinishEditing}
          canEdit={canEditContent}
          isEditing={isEditing}
          onOverflowSelected={onOverflowSelected}
        />
      );
    }

    case ItemType.note: {
      return (
        <CanvasNote
          containerWidth={containerWidth}
          item={item}
          index={globalIndex}
          {...spacingProps}
          draggable={canEditContent && !isEditing}
          onDragStart={handleReorderDragStart}
          onDragEnd={handleReorderDragEnd}
          isSelected={isSelected}
          onClick={onItemClicked}
          onDoubleClick={onItemDoubleClicked}
          onBeginEditing={onStartEditing}
          onFinishEditing={onFinishEditing}
          canEdit={canEditContent}
          canGenerateText={canGenerateText}
          isEditing={isEditing}
          onOverflowSelected={onOverflowSelected}
        />
      );
    }

    case ItemType.gallery: {
      return (
        <CanvasGallery
          containerWidth={containerWidth}
          {...spacingProps}
          item={item}
          index={globalIndex}
          isSelected={isSelected}
          canEdit={canEditContent}
          onClick={onItemClicked}
          onDoubleClick={onItemDoubleClicked}
          draggable={canEditContent}
          onDragStart={handleReorderDragStart}
          onDragEnd={handleReorderDragEnd}
          onOverflowSelected={onOverflowSelected}
        />
      );
    }

    default:
      return null;
  }
};

/**
 * Memoized version that only re-renders when non-selection props change.
 * Selection changes are handled internally via useIsItemSelected hook.
 */
export default memo(SelectableCanvasItem, (prevProps, nextProps) => {
  // Compare all props except callbacks (which should be stable)
  return (
    prevProps.item === nextProps.item &&
    prevProps.globalIndex === nextProps.globalIndex &&
    prevProps.editingItem === nextProps.editingItem &&
    prevProps.canEditContent === nextProps.canEditContent &&
    prevProps.guidePosition === nextProps.guidePosition &&
    prevProps.containerWidth === nextProps.containerWidth &&
    // Deep compare spacing props
    prevProps.spacingProps.mt === nextProps.spacingProps.mt &&
    prevProps.spacingProps.mb === nextProps.spacingProps.mb &&
    prevProps.spacingProps.pt === nextProps.spacingProps.pt &&
    prevProps.spacingProps.pb === nextProps.spacingProps.pb &&
    prevProps.spacingProps.px === nextProps.spacingProps.px &&
    prevProps.spacingProps.py === nextProps.spacingProps.py
  );
});
